import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

/**
 * Supabase Auth Context
 *
 * Provides centralized authentication state management for the entire app.
 * Subscribes to Supabase auth state changes and keeps session/user data in sync.
 */
const SupabaseAuthContext = createContext({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
});

/**
 * Hook to access Supabase auth context
 */
export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth must be used within SupabaseAuthProvider');
  }
  return context;
};

/**
 * Supabase Auth Provider Component
 *
 * Wraps the app to provide authentication state to all components.
 * Handles session hydration, auth state changes, and token refresh.
 */
export const SupabaseAuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hydrate session on mount before rendering children
    const initializeAuth = async () => {
      try {
        console.debug('[SupabaseAuthProvider] Hydrating session...');
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('[SupabaseAuthProvider] Error getting session:', error);
        }

        if (currentSession) {
          console.debug('[SupabaseAuthProvider] Session found:', {
            user: currentSession.user?.email,
            expires: new Date(currentSession.expires_at * 1000).toLocaleString(),
          });
          setSession(currentSession);
          setUser(currentSession.user);
        } else {
          console.debug('[SupabaseAuthProvider] No session found');
        }
      } catch (err) {
        console.error('[SupabaseAuthProvider] Unexpected error during init:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Subscribe to auth state changes (magic links, sign out, token refresh)
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.debug('[SupabaseAuthProvider] Auth state changed:', {
          event,
          user: currentSession?.user?.email,
          expires: currentSession?.expires_at
            ? new Date(currentSession.expires_at * 1000).toLocaleString()
            : null,
        });

        // Update state based on event
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          // Detect fresh email confirmation (Expert-recommended pattern)
          if (event === 'SIGNED_IN' && currentSession?.user?.email_confirmed_at) {
            const lastSignIn = sessionStorage.getItem('auth:lastSignIn');
            const wasJustConfirmed = !lastSignIn;

            if (wasJustConfirmed) {
              console.debug('[SupabaseAuthProvider] Fresh email confirmation detected');
              sessionStorage.setItem('auth:justConfirmed', 'true');
            }

            // Update last sign-in timestamp
            sessionStorage.setItem('auth:lastSignIn', Date.now().toString());
          }

          setSession(currentSession);
          setUser(currentSession?.user || null);
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          // Clear auth tracking flags on sign out
          sessionStorage.removeItem('auth:lastSignIn');
          sessionStorage.removeItem('auth:justConfirmed');

          setSession(null);
          setUser(null);
          setLoading(false);
        } else if (event === 'USER_UPDATED') {
          setUser(currentSession?.user || null);
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Sign out helper
  const signOut = async () => {
    try {
      console.debug('[SupabaseAuthProvider] Signing out...');
      await supabase.auth.signOut();
      // State will be updated by onAuthStateChange listener
    } catch (error) {
      console.error('[SupabaseAuthProvider] Error signing out:', error);
      throw error;
    }
  };

  const value = {
    session,
    user,
    loading,
    signOut,
  };

  return (
    <SupabaseAuthContext.Provider value={value}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};

export default SupabaseAuthProvider;
